'use client';

import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { VenueBookedTimeSlot } from '../../../shared/models';
import { useAvailabilityStore } from '../stores/availability_store';
import { motion, AnimatePresence } from 'framer-motion';

interface TimeRange {
    startTime: Date;
    endTime: Date;
    rangeId: number;
}

interface CircularTimeRangeSliderSelectorProps {
    timeRanges: TimeRange[];
    timesUnavailable: VenueBookedTimeSlot[];
    onTimeRangeChange: (startTime: Date, endTime: Date) => void;
    selectedStartTime: Date;
    onOverlapErrorChange: (hasError: boolean) => void;
}

export const CircularTimeRangeSliderSelector: React.FC<CircularTimeRangeSliderSelectorProps> = ({
    timeRanges,
    onTimeRangeChange,
    timesUnavailable,
    selectedStartTime,
    onOverlapErrorChange
}): React.ReactNode => {
    const { venue, selectedSection } = useAvailabilityStore();
    const CLOCK_SIZE = 150; // Slightly larger to ensure proper rendering
    const clockRef = useRef<HTMLDivElement>(null);

    const minimumBlocks = useMemo(() => {
        const minimumDuration = venue.PricingRatesPerSection[selectedSection]?.MinimumBookingDuration ?? 2;
        return minimumDuration * 2;
    }, [venue.PricingRatesPerSection, selectedSection]);

    // Generate 30-minute blocks
    const times = useMemo(() => {
        const blocks: TimeRange[] = [];

        timeRanges.forEach(range => {
            let currentTime = new Date(range.startTime);
            while (currentTime < range.endTime) {
                const blockEnd = new Date(currentTime.getTime() + 30 * 60000);
                blocks.push({
                    startTime: new Date(currentTime),
                    endTime: blockEnd > range.endTime ? range.endTime : blockEnd,
                    rangeId: currentTime.getTime()
                });
                currentTime = blockEnd;
            }
        });

        // Sort blocks by start time
        return blocks.sort((a, b) => a.startTime.getTime() - b.startTime.getTime());
    }, [timeRanges]);

    // Combine overlapping unavailable time slots
    const [mergedUnavailableTimes, setMergedUnavailableTimes] = useState<typeof timesUnavailable>([]);

    useEffect(() => {
        if (!timesUnavailable.length) {
            setMergedUnavailableTimes([]);
            return;
        }

        const sortedTimes = [...timesUnavailable].sort((a, b) =>
            new Date(a.StartTime).getTime() - new Date(b.StartTime).getTime()
        );

        const merged = [{ ...sortedTimes[0] }];
        const minimumGapMs = minimumBlocks * 30 * 60 * 1000; // Convert blocks to milliseconds

        for (let i = 1; i < sortedTimes.length; i++) {
            const current = sortedTimes[i];
            const previous = merged[merged.length - 1];
            const currentStartTime = new Date(current.StartTime).getTime();
            const previousEndTime = new Date(previous.EndTime).getTime();
            const gapBetweenSlots = currentStartTime - previousEndTime;

            if (currentStartTime <= previousEndTime || gapBetweenSlots < minimumGapMs) {
                // Merge slots if they overlap or if gap is less than minimum blocks
                previous.EndTime = new Date(Math.max(
                    previousEndTime,
                    new Date(current.EndTime).getTime()
                ));
            } else {
                merged.push({ ...current });
            }
        }
        setMergedUnavailableTimes(merged);
    }, [timesUnavailable, minimumBlocks]);

    // Improved time to angle conversion helpers
    const getAngleFromTime = (time: Date) => {
        const hours = time.getHours();
        const minutes = time.getMinutes();
        const totalHours = hours + (minutes / 60);
        // Convert time to angle (0 hours = 0 degrees, 24 hours = 360 degrees)
        return (totalHours / 24) * 360;
    };

    // Determine if a time is within available ranges
    const isTimeAvailable = useCallback((time: Date) => {
        return times.some(range =>
            time >= range.startTime && time <= range.endTime
        );
    }, [times]);

    // Check if time is within unavailable time slots
    const isTimeUnavailable = useCallback((time: Date) => {
        return mergedUnavailableTimes.some(slot => {
            const slotStart = new Date(slot.StartTime);
            const slotEnd = new Date(slot.EndTime);

            // Handle wraparound cases (midnight crossing)
            if (slotEnd < slotStart) {
                // For slots that wrap around midnight
                return time >= slotStart || time <= slotEnd;
            }

            return time >= slotStart && time <= slotEnd;
        });
    }, [mergedUnavailableTimes]);

    const getPercentFromTime = useCallback((time: Date) => {
        if (times.length === 0) return 0;

        const totalTimeRange = times[times.length - 1].endTime.getTime() - times[0].startTime.getTime();
        const timeOffset = time.getTime() - times[0].startTime.getTime();
        return (timeOffset / totalTimeRange) * 100;
    }, [times]);

    const getTimeFromPercent = useCallback((percent: number) => {
        if (times.length === 0) return new Date();

        const totalTimeRange = times[times.length - 1].endTime.getTime() - times[0].startTime.getTime();
        const timeOffset = (percent / 100) * totalTimeRange;
        return new Date(times[0].startTime.getTime() + timeOffset);
    }, [times]);

    const [range, setRange] = useState<number[]>(() => {
        const totalSize = times.length;
        const blockSize = 100 / totalSize;
        const minRangeSize = minimumBlocks * blockSize;

        // If selectedStartTime is provided, use it as the start
        if (selectedStartTime && times.length > 0) {
            const startPercent = getPercentFromTime(selectedStartTime);
            const endPercent = Math.min(100, startPercent + minRangeSize);
            return [startPercent, endPercent];
        }

        // Handle case where times array is too small
        if (times.length <= minimumBlocks) {
            return [0, 100];
        }

        // Convert unavailable times to percentages
        const unavailableRanges = mergedUnavailableTimes.map(slot => ({
            start: getPercentFromTime(new Date(slot.StartTime)),
            end: getPercentFromTime(new Date(slot.EndTime))
        }));

        // Try to find a range starting from the middle that doesn't overlap
        const middleIndex = Math.floor(totalSize / 2);
        let initialStart = Math.max(0, middleIndex - Math.floor(minimumBlocks / 2)) * blockSize;
        let initialEnd = Math.min(100, initialStart + minRangeSize);

        // Check if middle position works
        const middleRangeOverlaps = unavailableRanges.some(range =>
            !(initialEnd <= range.start || initialStart >= range.end)
        );

        if (!middleRangeOverlaps) {
            return [initialStart, initialEnd];
        }

        // If middle doesn't work, scan from start to find first valid range
        for (let i = 0; i <= totalSize - minimumBlocks; i++) {
            const validStart = i * blockSize;
            const validEnd = validStart + minRangeSize;

            const overlaps = unavailableRanges.some(range =>
                !(validEnd <= range.start || validStart >= range.end)
            );

            if (!overlaps) {
                return [validStart, validEnd];
            }
        }

        // If no valid range found, return the original middle position
        return [initialStart, initialEnd];
    });

    const [isInvalid, setIsInvalid] = useState(false);
    const [overlapError, setOverlapError] = useState(false);

    const formatTime = (date: Date) => {
        return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    };

    // Generate list of available time options for dropdowns
    const timeOptions = useMemo(() => {
        if (times.length === 0) return [];

        // Create time options at 30-minute intervals
        const options: { value: string, label: string, date: Date, isUnavailable: boolean }[] = [];
        let currentTime = new Date(times[0].startTime);

        while (currentTime <= times[times.length - 1].endTime) {
            options.push({
                value: currentTime.toISOString(),
                label: formatTime(currentTime),
                date: new Date(currentTime),
                isUnavailable: isTimeUnavailable(currentTime)
            });

            // Add 30 minutes
            currentTime = new Date(currentTime.getTime() + 30 * 60000);
        }

        return options;
    }, [times, formatTime, isTimeUnavailable]);

    // Calculate current start and end time based on range percentages
    const startTime = useMemo(() => getTimeFromPercent(range[0]), [getTimeFromPercent, range]);
    const endTime = useMemo(() => getTimeFromPercent(range[1]), [getTimeFromPercent, range]);

    // Effect to handle minimum blocks changes
    useEffect(() => {
        if (times.length > 0) {
            const totalSize = times.length;
            const blockSize = 100 / totalSize;
            const minRangeSize = minimumBlocks * blockSize;

            setRange(prev => {
                const currentRangeSize = prev[1] - prev[0];
                if (currentRangeSize < minRangeSize) {
                    const center = (prev[0] + prev[1]) / 2;
                    const newStart = Math.max(0, center - (minRangeSize / 2));
                    const newEnd = Math.min(100, newStart + minRangeSize);
                    return [newStart, newEnd];
                }
                return prev;
            });
        }
    }, [minimumBlocks, times.length]);

    // Modify the timeRangeChange effect to use a ref for tracking changes
    const pendingTimeRangeUpdate = useRef<{ startTime: Date, endTime: Date } | null>(null);

    // Replace the existing useEffect for time range changes
    useEffect(() => {
        if (pendingTimeRangeUpdate.current) {
            // Use requestAnimationFrame to ensure we're not updating during render
            const frameId = requestAnimationFrame(() => {
                if (pendingTimeRangeUpdate.current) {
                    onTimeRangeChange(
                        pendingTimeRangeUpdate.current.startTime,
                        pendingTimeRangeUpdate.current.endTime
                    );
                    pendingTimeRangeUpdate.current = null;
                }
            });

            return () => cancelAnimationFrame(frameId);
        }
    }, [onTimeRangeChange]);

    useEffect(() => {
        if (times.length === 0) return;

        const startTime = getTimeFromPercent(range[0]);
        const endTime = getTimeFromPercent(range[1]);

        // Create dayStart and dayEnd for overlap calculations
        const dayStart = new Date(startTime);
        dayStart.setHours(0, 0, 0, 0);

        const dayEnd = new Date(dayStart);
        dayEnd.setHours(24, 0, 0, 0);

        // Check for overlaps with unavailable times
        const hasOverlap = mergedUnavailableTimes.some(unavailableTime => {
            const unavailStart = new Date(unavailableTime.StartTime);
            const unavailEnd = new Date(unavailableTime.EndTime);

            // Handle wraparound for unavailable times
            const unavailCrossesMidnight = unavailEnd < unavailStart ||
                (unavailEnd.getHours() === 0 && unavailEnd.getMinutes() === 0 && unavailStart.getHours() > 0);

            if (unavailCrossesMidnight) {
                // For unavailable times that cross midnight, check both parts
                return (startTime < unavailEnd && unavailStart < dayEnd) ||
                    (dayStart < endTime && startTime < unavailEnd);
            }

            // Standard overlap check - the periods overlap if one starts before the other ends
            return (startTime < unavailEnd && unavailStart < endTime);
        });

        setOverlapError(hasOverlap);
        onOverlapErrorChange?.(hasOverlap);

        // Calculate how the time range compares to minimum requirements
        const minimumRangeRequired = minimumBlocks * 100 / times.length;
        let isInvalidRange = false;

        // Only check duration validity when end time is after start time (normal case)
        if (endTime > startTime) {
            const rangeDifference = range[1] - range[0];
            // Round values to handle floating point precision
            const roundedRangeDiff = Math.round(rangeDifference * 1000000) / 1000000;
            const roundedMinRequired = Math.round(minimumRangeRequired * 1000000) / 1000000;

            isInvalidRange = roundedRangeDiff < roundedMinRequired;
        } else {
            // When end time is before start time, we're in an invalid state automatically
            isInvalidRange = true;
        }

        setIsInvalid(isInvalidRange);
    }, [range, mergedUnavailableTimes, minimumBlocks, times.length, getTimeFromPercent, onOverlapErrorChange]);

    // Generate hour marks and labels for clock face
    const hourMarks = useMemo(() => {
        const marks = [];

        // Always show 24 hours on the clock regardless of available times
        for (let hour = 0; hour < 24; hour++) {
            const angle = (hour / 24) * 360;

            // Hour tick mark - make more visible
            if (hour % 3 === 0) {
                marks.push(
                    <div
                        key={`mark-${hour}`}
                        className="absolute w-0.5 h-1.5 bg-gray-500"
                        style={{
                            top: '2px',
                            left: '50%',
                            transformOrigin: `50% calc(100% + ${CLOCK_SIZE / 2 - 2}px)`,
                            transform: `translateX(-50%) rotate(${angle}deg)`
                        }}
                    />
                );
            }

            // Only show 6-hour interval labels
            if (hour % 6 === 0) {
                const labelAngle = (angle - 90) * (Math.PI / 180); // -90 to start at 12 o'clock
                const labelRadius = CLOCK_SIZE * 0.38; // Position inside the clock face
                const x = CLOCK_SIZE / 2 + labelRadius * Math.cos(labelAngle);
                const y = CLOCK_SIZE / 2 + labelRadius * Math.sin(labelAngle);

                marks.push(
                    <div
                        key={`label-${hour}`}
                        className="absolute text-[8px] font-medium text-gray-400 select-none z-10"
                        style={{
                            top: `${y}px`,
                            left: `${x}px`,
                            transform: 'translate(-50%, -50%)'
                        }}
                    >
                        {hour === 0 ? '12A' : hour > 12 ? `${hour - 12}P` : hour === 12 ? '12P' : `${hour}A`}
                    </div>
                );
            }
        }

        return marks;
    }, [CLOCK_SIZE]);

    // Generate unavailable time blocks based on full 24 hour clock
    const fullDayUnavailableBlocks = useMemo(() => {
        // Only use the merged unavailable time slots (actual bookings)
        // and don't include venue closed hours
        return [...mergedUnavailableTimes];
    }, [mergedUnavailableTimes]);

    // Calculate start and end angles for the selected time range
    const startAngle = getAngleFromTime(startTime);
    const endAngle = getAngleFromTime(endTime);

    // Handle dropdown changes
    const handleStartTimeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const selectedTimeISO = e.target.value;
        const selectedTime = new Date(selectedTimeISO);
        const startPercent = getPercentFromTime(selectedTime);

        // Calculate the minimum required duration
        const totalSize = times.length;
        const blockSize = 100 / totalSize;
        const minRangeSize = minimumBlocks * blockSize;
        const minimumDurationMs = minimumBlocks * 30 * 60 * 1000;

        // Find the ideal end time (exactly minimum duration after start)
        const idealEndTime = new Date(selectedTime.getTime() + minimumDurationMs);

        // Find the first valid end time that is at or after the ideal end time
        let bestEndTimeIndex = -1;
        let smallestDifference = Number.MAX_VALUE;

        for (let i = 0; i < timeOptions.length; i++) {
            const option = timeOptions[i];
            if (option.isUnavailable) continue;

            const optionDate = new Date(option.date);
            const timeDiff = optionDate.getTime() - idealEndTime.getTime();

            // Only consider times after or at the ideal end time
            if (timeDiff >= 0 && timeDiff < smallestDifference) {
                smallestDifference = timeDiff;
                bestEndTimeIndex = i;
            }
        }

        // If we found a valid end time
        if (bestEndTimeIndex !== -1) {
            const bestEndTime = new Date(timeOptions[bestEndTimeIndex].date);
            const endPercent = getPercentFromTime(bestEndTime);
            setRange([startPercent, endPercent]);
        } else {
            // Fallback: try to select minimum duration or closest value
            const endPercent = Math.min(100, startPercent + minRangeSize);
            setRange([startPercent, endPercent]);
        }
    };

    const handleEndTimeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const selectedTimeISO = e.target.value;
        const selectedTime = new Date(selectedTimeISO);
        const endPercent = getPercentFromTime(selectedTime);
        const startPercent = range[0];

        // We allow selecting any end time, even if it's before the start time,
        // but the validation will mark it as invalid in that case.
        // This gives users flexibility in how they want to select times.

        setRange([startPercent, endPercent]);
    };

    // Add back the useEffect to stage updates
    useEffect(() => {
        if (times.length === 0) return;

        const startTime = getTimeFromPercent(range[0]);
        const endTime = getTimeFromPercent(range[1]);

        // Stage the update instead of calling it directly
        pendingTimeRangeUpdate.current = { startTime, endTime };
    }, [range, getTimeFromPercent, times.length]);

    // Create the clip path for the selected time slice
    const getClipPath = (startAngle: number, endAngle: number, isWraparound = false): string => {
        const centerX = CLOCK_SIZE / 2;
        const centerY = CLOCK_SIZE / 2;
        const radius = CLOCK_SIZE / 2;

        // If end time is before start time, show a "reverse" selection that looks different
        // to indicate it's an invalid selection
        if (endAngle < startAngle) {
            // Draw a small arc in the opposite direction to visually indicate the issue
            const tempAngle = startAngle;
            startAngle = endAngle;
            endAngle = tempAngle;

            // Ensure the arc is visible but small
            if (endAngle - startAngle > 90) {
                endAngle = startAngle + 90;
            }
        }

        const startRad = (startAngle - 90) * Math.PI / 180;
        const endRad = (endAngle - 90) * Math.PI / 180;

        const startX = centerX + radius * Math.cos(startRad);
        const startY = centerY + radius * Math.sin(startRad);
        const endX = centerX + radius * Math.cos(endRad);
        const endY = centerY + radius * Math.sin(endRad);

        const largeArcFlag = endAngle - startAngle > 180 ? 1 : 0;

        // Use CSS clip-path with path() function
        return `path("M${centerX},${centerY} L${startX},${startY} A${radius},${radius} 0 ${largeArcFlag},1 ${endX},${endY} Z")`;
    };

    return (
        <div className="w-full flex flex-col items-center">
            <div className="w-full">
                <div className="relative flex flex-col justify-center items-center">
                    {/* Clock container */}
                    <div
                        ref={clockRef}
                        className="relative w-[150px] h-[150px] mx-auto"
                    >
                        {/* Clock face - improve visibility */}
                        <div className="relative w-full h-full rounded-full bg-gray-900 border border-gray-700 shadow-lg overflow-hidden">
                            {/* Hour marks and labels */}
                            {hourMarks}

                            {/* Unavailable time slots - improve visibility */}
                            {fullDayUnavailableBlocks.map((slot, index) => {
                                const slotStartTime = new Date(slot.StartTime);
                                const slotEndTime = new Date(slot.EndTime);

                                // Special case: if the slot is a full 24-hour period (like 00:00 to 00:00)
                                // or if it would cover the entire clock, skip rendering it
                                if ((slotStartTime.getHours() === 0 && slotStartTime.getMinutes() === 0 &&
                                    slotEndTime.getHours() === 0 && slotEndTime.getMinutes() === 0) ||
                                    (slotEndTime.getTime() - slotStartTime.getTime() >= 24 * 60 * 60 * 1000)) {
                                    return null;
                                }

                                const slotStartAngle = getAngleFromTime(slotStartTime);
                                const slotEndAngle = getAngleFromTime(slotEndTime);

                                // Enhanced wraparound detection
                                const isWraparound = slotEndTime < slotStartTime ||
                                    slotEndAngle < slotStartAngle ||
                                    (slotEndTime.getHours() === 0 && slotEndTime.getMinutes() === 0 &&
                                        slotStartTime.getHours() > 0);

                                // For wraparound slots that end very late (e.g., 2 AM),
                                // adjust the end time if it's being rendered incorrectly
                                let clipPathValue = getClipPath(slotStartAngle, slotEndAngle, isWraparound);

                                return (
                                    <div
                                        key={index}
                                        className="absolute top-0 left-0 w-full h-full"
                                    >
                                        <div
                                            className="absolute top-0 left-0 w-full h-full bg-red-500/20"
                                            style={{ clipPath: clipPathValue as any }}
                                        ></div>
                                        <div
                                            className="absolute top-0 left-0 w-full h-full"
                                            style={{
                                                clipPath: clipPathValue as any,
                                                backgroundImage: 'repeating-linear-gradient(45deg, transparent, transparent 5px, rgba(255, 77, 77, 0.2) 5px, rgba(255, 77, 77, 0.2) 10px)'
                                            }}
                                        ></div>
                                    </div>
                                );
                            })}

                            {/* Selected time range - improve visibility */}
                            <motion.div
                                className={`absolute top-0 left-0 w-full h-full overflow-hidden ${isInvalid || overlapError ? 'bg-red-500/60' : 'bg-purple-500/70'}`}
                                style={{
                                    clipPath: getClipPath(
                                        startAngle,
                                        endAngle,
                                        false
                                    ) as any,
                                }}
                                initial={{ opacity: 0.7 }}
                                animate={{
                                    opacity: [0.7, 0.9, 0.7],
                                }}
                                transition={{
                                    repeat: Infinity,
                                    duration: 2
                                }}
                            />

                            {/* Clock center */}
                            <div className="absolute w-2 h-2 bg-purple-400 rounded-full top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 z-10"></div>
                        </div>
                    </div>

                    {/* Time selection dropdowns - horizontal layout with labels above */}
                    <div className="flex flex-row items-end justify-between w-full max-w-[330px] mt-3 px-2">
                        <div className="flex flex-col items-center">
                            <div className="text-xs text-gray-400 mb-1">Start</div>
                            <select
                                id="start-time"
                                value={startTime.toISOString()}
                                onChange={handleStartTimeChange}
                                className="w-[120px] bg-gray-800 text-white text-sm border border-gray-700 rounded-md py-2 px-3 focus:outline-none focus:ring-1 focus:ring-purple-500 focus:border-transparent appearance-none"
                                style={{
                                    backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='%239ca3af'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M19 9l-7 7-7-7'%3E%3C/path%3E%3C/svg%3E")`,
                                    backgroundRepeat: 'no-repeat',
                                    backgroundPosition: 'right 0.5rem center',
                                    backgroundSize: '1em 1em',
                                    paddingRight: '2rem'
                                }}
                            >
                                {timeOptions
                                    .filter(option => {
                                        // Filter out unavailable times
                                        if (option.isUnavailable) return false;

                                        // Calculate if this start time would have enough room for minimum duration
                                        const optionDate = new Date(option.date);
                                        const minimumDurationMs = minimumBlocks * 30 * 60 * 1000;

                                        // Calculate the latest possible end time for all available time ranges
                                        const latestEndTime = times.length > 0 ? times[times.length - 1].endTime : new Date();

                                        // Check if there's enough time between this option and the latest end time
                                        const timeUntilEnd = latestEndTime.getTime() - optionDate.getTime();

                                        // If there's not enough time until the end, this start time isn't valid
                                        if (timeUntilEnd < minimumDurationMs) {
                                            return false;
                                        }

                                        // Also check if there are any unavailable times within the minimum booking window
                                        const potentialEndTime = new Date(optionDate.getTime() + minimumDurationMs);

                                        // Check every 30-minute increment for availability
                                        let currentTimeCheck = new Date(optionDate);
                                        while (currentTimeCheck < potentialEndTime) {
                                            if (isTimeUnavailable(currentTimeCheck)) {
                                                return false; // There's an unavailable time in the required duration
                                            }
                                            // Move to next 30-min increment
                                            currentTimeCheck = new Date(currentTimeCheck.getTime() + 30 * 60000);
                                        }

                                        // This start time has enough room for minimum duration with no conflicts
                                        return true;
                                    })
                                    .map((option) => (
                                        <option
                                            key={option.value}
                                            value={option.value}
                                        >
                                            {option.label}
                                        </option>
                                    ))}
                            </select>
                        </div>

                        <div className="text-gray-400 font-normal text-sm pb-2">to</div>

                        <div className="flex flex-col items-center">
                            <div className="text-xs text-gray-400 mb-1">End</div>
                            <select
                                id="end-time"
                                value={endTime.toISOString()}
                                onChange={handleEndTimeChange}
                                className="w-[120px] bg-gray-800 text-white text-sm border border-gray-700 rounded-md py-2 px-3 focus:outline-none focus:ring-1 focus:ring-purple-500 focus:border-transparent appearance-none"
                                style={{
                                    backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='%239ca3af'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M19 9l-7 7-7-7'%3E%3C/path%3E%3C/svg%3E")`,
                                    backgroundRepeat: 'no-repeat',
                                    backgroundPosition: 'right 0.5rem center',
                                    backgroundSize: '1em 1em',
                                    paddingRight: '2rem'
                                }}
                            >
                                {timeOptions
                                    .filter(option => {
                                        // Filter out unavailable times
                                        if (option.isUnavailable) return false;

                                        // Only show end times that are after start time
                                        const optionDate = new Date(option.date);
                                        const minimumDurationMs = minimumBlocks * 30 * 60 * 1000;

                                        // Check if this option is at least minimum duration after start time
                                        const timeFromStart = optionDate.getTime() - startTime.getTime();

                                        // Only show end times that satisfy minimum duration
                                        return timeFromStart >= minimumDurationMs;
                                    })
                                    .map((option) => {
                                        const optionDate = new Date(option.date);
                                        const minimumDurationMs = minimumBlocks * 30 * 60 * 1000;
                                        const timeFromStart = optionDate.getTime() - startTime.getTime();

                                        // Check if this is the minimum allowed duration (or very close to it)
                                        const isMinimumDuration = Math.abs(timeFromStart - minimumDurationMs) < 60000; // Within 1 minute

                                        return (
                                            <option
                                                key={option.value}
                                                value={option.value}
                                            >
                                                {option.label}
                                                {isMinimumDuration ? ' (Min)' : ''}
                                            </option>
                                        );
                                    })}
                            </select>
                        </div>
                    </div>
                </div>
            </div>

            {/* Minimum booking duration info */}
            <div className="text-xs text-gray-400 mt-2 text-center">
                Minimum booking: {minimumBlocks * 0.5} hours
            </div>

            {/* Compact error messages */}
            <AnimatePresence>
                {(isInvalid || overlapError) && (
                    <motion.div
                        initial={{ opacity: 0, y: -5 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -5 }}
                        className="w-full mt-1 mb-1 py-1 px-2 bg-red-900/30 border border-red-600 text-red-200 rounded-md text-xs"
                    >
                        {isInvalid && (
                            endTime <= startTime
                                ? `Start time must be before end time`
                                : `Need minimum ${minimumBlocks * 0.5} hours`
                        )}
                        {overlapError && !isInvalid && `Selected time overlaps with unavailable times`}
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
}; 